import type { CSSProperties, ReactNode } from 'react';
import { cn } from '../../../lib/utils';
import { SanityImage } from '../../../sanity/utils/components';
import type { SanityImageProps } from '../../../sanity/utils/fragments/image-fragment';
import { CustomLink } from '../../custom-link';
import { ColorSwatch } from '../../ui/color-swatch';

type ColorValue = {
  type: 'color';
  title: string;
  hexCode?: string | null;
  image?: SanityImageProps | null;
};

type FinishValue = {
  type: 'finish';
  title: string;
  image?: SanityImageProps | null;
};

type SizeValue = {
  type: 'size';
  title: string;
  image?: SanityImageProps | null;
};

type AttributeType = 'color' | 'finish' | 'size';

type ProductVariant = {
  slug: string;
  color: ColorValue | null;
  finish: FinishValue | null;
  size: SizeValue | null;
  category: string | null;
};

interface ProductVariantsProps {
  productFamily: {
    products: ProductVariant[];
  };
  currentSlug: string;
  className?: string;
  style?: CSSProperties;
}

export function ProductDetailsVariants({
  productFamily,
  currentSlug,
  className,
  style,
}: ProductVariantsProps) {
  if (!productFamily) return null;

  const currentProduct = productFamily.products.find(
    (p) => p.slug === currentSlug
  );

  if (!currentProduct) return null;

  const attributes: AttributeType[] = ['color', 'finish', 'size'];

  return (
    <div
      className={cn('col-span-full grid gap-4', className)}
      aria-label="Product variant selectors"
      style={style}
    >
      {/* biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation> */}
      {attributes.map((attribute, index) => {
        // Deduplicate options by stringified value (title/hex for color, title for others)
        const seen = new Set<string>();
        const options: {
          value: ColorValue | FinishValue | SizeValue;
          product: ProductVariant;
        }[] = [];

        for (const prod of productFamily.products) {
          const value = prod[attribute as AttributeType];
          if (!value) continue;

          let key = '';
          if (isColorValue(value)) {
            key = value?.hexCode || value?.title || '';
          } else if (isFinishValue(value) || isSizeValue(value)) {
            key = value?.title || '';
          }
          if (!key || seen.has(key)) continue;

          // Build a selection object for matching
          const selection: Record<
            AttributeType,
            ColorValue | FinishValue | SizeValue | null
          > = {
            color: currentProduct.color,
            finish: currentProduct.finish,
            size: currentProduct.size,
          };
          selection[attribute as AttributeType] = value;

          // Find a product that matches this selection
          const match = productFamily.products.find((p) => {
            return (
              (!selection.color ||
                !p.color ||
                JSON.stringify(selection.color) === JSON.stringify(p.color)) &&
              (!selection.finish ||
                !p.finish ||
                JSON.stringify(selection.finish) ===
                  JSON.stringify(p.finish)) &&
              (!selection.size ||
                !p.size ||
                JSON.stringify(selection.size) === JSON.stringify(p.size))
            );
          });
          if (!match) continue;

          seen.add(key);
          options.push({ value, product: match });
        }

        // Sort options with smart comparison for dimensions
        options.sort((a, b) => {
          const aTitle = a.value.title || '';
          const bTitle = b.value.title || '';

          const aDimensions = parseDimensions(aTitle);
          const bDimensions = parseDimensions(bTitle);

          // If both have valid dimensions, sort by area (smallest to largest)
          if (aDimensions.isValid && bDimensions.isValid) {
            return aDimensions.area - bDimensions.area;
          }

          // If only one has dimensions, put dimensional ones first
          if (aDimensions.isValid && !bDimensions.isValid) return -1;
          if (!aDimensions.isValid && bDimensions.isValid) return 1;

          // Fall back to alphabetical for non-dimensional titles
          return aTitle.localeCompare(bTitle);
        });

        if (options.length === 0) return null;

        // Get current value for this attribute
        const currentValue = currentProduct[attribute as AttributeType];
        const firstValue = options[0]?.value;

        if (firstValue && isColorValue(firstValue)) {
          return (
            <VariantSelectorGroup
              key={`${attribute}-${index}`}
              label={'Colour'}
            >
              <ColorSelector
                options={
                  options as { value: ColorValue; product: ProductVariant }[]
                }
                currentValue={isColorValue(currentValue) ? currentValue : null}
              />
            </VariantSelectorGroup>
          );
        }

        if (firstValue && isFinishValue(firstValue)) {
          return (
            <VariantSelectorGroup
              key={`${attribute}-${index}`}
              label={attribute}
            >
              <TextSelector
                options={
                  options as { value: FinishValue; product: ProductVariant }[]
                }
                currentValue={isFinishValue(currentValue) ? currentValue : null}
              />
            </VariantSelectorGroup>
          );
        }

        if (firstValue && isSizeValue(firstValue)) {
          return (
            <VariantSelectorGroup
              key={`${attribute}-${index}`}
              label={attribute}
            >
              <ImageSelector
                options={
                  options as { value: SizeValue; product: ProductVariant }[]
                }
                currentValue={isSizeValue(currentValue) ? currentValue : null}
                category={productFamily.products[0]?.category || ''}
              />
            </VariantSelectorGroup>
          );
        }

        return null;
      })}
    </div>
  );
}

function ColorSelector({
  options,
  currentValue,
}: {
  options: { value: ColorValue; product: ProductVariant }[];
  currentValue: ColorValue | null;
}) {
  return (
    <div aria-label="Colour">
      <div className="flex flex-wrap gap-2">
        {options.map(({ value, product }, index) => {
          const isSelected =
            currentValue &&
            value &&
            JSON.stringify(currentValue) === JSON.stringify(value);
          const slug = getSlug(product.slug);

          return (
            <CustomLink
              key={`${product.slug}-${index}`}
              href={slug}
              aria-current={isSelected ? 'true' : undefined}
              className="inline-flex"
            >
              <ColorSwatch
                hexColor={isColorValue(value) ? value?.hexCode : undefined}
                image={isColorValue(value) ? value?.image : undefined}
                label={
                  isColorValue(value) ? value?.title || 'Colour' : 'Colour'
                }
                isSelected={!!isSelected}
                size="lg"
              />
            </CustomLink>
          );
        })}
      </div>
    </div>
  );
}

function TextSelector({
  options,
  currentValue,
}: {
  options: { value: FinishValue; product: ProductVariant }[];
  currentValue: FinishValue | null;
}) {
  return (
    <div aria-label="Finish">
      <div className="flex flex-wrap gap-2">
        {options.map(({ value, product }, index) => {
          const isSelected =
            currentValue &&
            value &&
            JSON.stringify(currentValue) === JSON.stringify(value);
          const title = isFinishValue(value) ? value?.title : '';
          const slug = getSlug(product.slug);

          return (
            <CustomLink
              key={`${product.slug}-${index}`}
              href={slug}
              aria-current={isSelected ? 'true' : undefined}
              className={cn(
                'border border-gray-300 px-4 py-2 font-bold text-[hsl(var(--color))] uppercase',
                isSelected && 'border-[hsl(var(--color))]'
              )}
            >
              {title}
            </CustomLink>
          );
        })}
      </div>
    </div>
  );
}

function ImageSelector({
  options,
  currentValue,
  category,
}: {
  options: { value: SizeValue; product: ProductVariant }[];
  currentValue: SizeValue | null;
  category: string;
}) {
  return (
    <div aria-label="Size">
      <div className="flex flex-wrap gap-2">
        {/* biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation> */}
        {options.map(({ value, product }, index) => {
          const isSelected =
            currentValue &&
            value &&
            JSON.stringify(currentValue) === JSON.stringify(value);
          const image = isSizeValue(value) ? value?.image : undefined;
          const title = isSizeValue(value) ? value?.title : '';
          const slug = getSlug(product.slug);
          const isToilets = category?.toLowerCase().trim() === 'toilets';

          return (
            <CustomLink
              key={`${product.slug}-${index}`}
              href={slug}
              aria-current={isSelected ? 'true' : undefined}
              className={cn(
                'flex flex-col items-center border p-2.5 pb-1.5 font-semibold text-[hsl(var(--color))] text-xs',
                isToilets ? 'min-w-40' : 'min-w-16',
                isSelected
                  ? 'border-[hsl(var(--color))]'
                  : 'border-[hsl(var(--color))]/50'
              )}
            >
              {image ? (
                <SanityImage
                  image={image}
                  width={isToilets ? 150 : 50}
                  height={50}
                  noLQIP
                  className={cn(
                    'max-h-[50px] shrink-0',
                    isToilets ? 'max-w-[150px]' : 'max-w-[50px]'
                  )}
                />
              ) : (
                <div
                  className={cn(
                    'h-[50px]',
                    isToilets ? 'w-[150px]' : 'w-[50px]'
                  )}
                />
              )}
              <span className="whitespace-nowrap px-1 text-center">
                {title}
              </span>
            </CustomLink>
          );
        })}
      </div>
    </div>
  );
}

function getSlug(slug: string) {
  return `/products/${slug}`;
}

function isColorValue(value: unknown): value is ColorValue {
  return (
    !!value &&
    typeof value === 'object' &&
    (value as ColorValue)?.type === 'color'
  );
}

function isFinishValue(value: unknown): value is FinishValue {
  return (
    !!value &&
    typeof value === 'object' &&
    (value as FinishValue)?.type === 'finish'
  );
}

function isSizeValue(value: unknown): value is SizeValue {
  return (
    !!value &&
    typeof value === 'object' &&
    (value as SizeValue)?.type === 'size'
  );
}

const MATCH_REGEX = /(\d+(?:\.\d+)?)\s*[x×X]\s*(\d+(?:\.\d+)?)/;

function parseDimensions(title: string): { area: number; isValid: boolean } {
  // Remove common units and extra whitespace
  const cleaned = title
    .toLowerCase()
    .replace(/mm|cm|m|inches?|in|ft/g, '')
    .trim();

  // Look for pattern like "600x1200", "600 x 1200", "600×1200", "600X1200"
  const match = cleaned.match(MATCH_REGEX);

  if (match?.[1] && match?.[2]) {
    const width = Number.parseFloat(match[1]);
    const height = Number.parseFloat(match[2]);
    return { area: width * height, isValid: true };
  }

  return { area: 0, isValid: false };
}

function VariantSelectorGroup({
  label,
  children,
}: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center gap-4">
      <h3 className="min-w-[50px] font-semibold text-[hsl(var(--color))] text-sm uppercase">
        {label}
      </h3>
      <fieldset className="group m-0 flex min-w-0 max-w-full flex-wrap gap-2 border-0 p-0">
        {children}
      </fieldset>
    </div>
  );
}
